import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Image,
  Modal,
  Col,
  Button,
  Tooltip,
  Checkbox,
  Row,
  Form,
  Radio,
  Menu,
  Dropdown,
} from 'antd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  PicRightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const VehicleImageItem = ({
  image,
  deleteImage,
  setAsPreview,
  tags,
  setTags,
  setImageType,
  onImageLoad,
}) => {
  const { t } = useTranslation();
  const loggedInUser = useSelector((state) => state.auth.loggedInUser);
  const { confirm } = Modal;

  const [form] = Form.useForm();
  const [isTagsModalVisible, setIsTagsModalVisible] = useState(false);
  const [isTypeModalVisible, setIsTypeModalVisible] = useState(false);

  const showConfirm = () => {
    confirm({
      title: t('vehicle.messages.imageDeleteConfirm'),
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      maskClosable: true,
      onOk() {
        deleteImage(image.id);
      },
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
    });
  };

  const showConfirmSetImage = () => {
    confirm({
      title: t('vehicle.messages.previewConfirm'),
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      maskClosable: true,
      onOk() {
        setAsPreview(image.id);
      },
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
    });
  };

  const showTagsModal = () => {
    const initialTagIds = image.tags ? image.tags.map((tag) => tag.id) : [];
    form.setFieldsValue({ selectedTags: initialTagIds });
    setIsTagsModalVisible(true);
  };

  const handleTagsSubmit = () => {
    const selectedTags = form.getFieldValue('selectedTags');
    setTags(image.id, selectedTags);
    setIsTagsModalVisible(false);
  };

  const showTypeModal = () => {
    form.setFieldsValue({ selectedType: image.type || '' }); // Set the current type of the image (if it exists)
    setIsTypeModalVisible(true);
  };

  const handleTypeSubmit = () => {
    const selectedType = form.getFieldValue('selectedType');
    setImageType(image.id, selectedType);
    setIsTypeModalVisible(false);
  };

  const onDownload = () => {
    const url = image.path;
    const suffix = url.slice(url.lastIndexOf('.'));
    const filename = Date.now() + suffix;

    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(blobUrl);
        link.remove();
        //   message.success('Image has been downloaded!');
      })
      .catch((error) => {
        //message.error('Failed to download image.');
      });
  };

  // Menu items for the dropdown
  const items = [
    {
      label: t('vehicle.actions.preview'),
      key: 'setPreview',
      icon: <CheckCircleOutlined />,
      onClick: showConfirmSetImage,
    },
    {
      label: t('vehicle.actions.setTags'),
      key: 'setTags',
      icon: <PicRightOutlined />,
      onClick: showTagsModal,
    },
    {
      label: t('vehicle.actions.setType'),
      key: 'setType',
      icon: <InfoCircleOutlined />,
      onClick: showTypeModal,
    },
    {
      label: t('common.delete'),
      key: 'delete',
      icon: <DeleteOutlined />,
      onClick: showConfirm,
      danger: true, // For the delete button to make it red
    },
  ];

  return (
    <Col
      xs={24}
      sm={12}
      md={12}
      lg={12}
      xl={8}
      style={{ position: 'relative' }}
    >
      {!(
        loggedInUser.role.name === 'user' ||
        loggedInUser.role.name === 'carHouseAdmin'
      ) && (
        <Dropdown.Button
          style={{
            position: 'absolute',
            right: '10px',
            top: '3px',
            zIndex: '3',
          }}
          overlay={<Menu mode="vertical" items={items} />}
        ></Dropdown.Button>
      )}
      <div style={{ position: 'relative' }}>
        <Image src={image.path} alt={image.name} onLoad={onImageLoad} />
      </div>

      {/* Tags Modal */}
      <Modal
        title={t('vehicle.actions.selectTags')}
        open={isTagsModalVisible}
        onCancel={() => setIsTagsModalVisible(false)}
        onOk={handleTagsSubmit}
        okText={t('common.ok')}
        cancelText={t('common.cancel')}
      >
        <Form form={form}>
          <Form.Item name="selectedTags">
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                {tags.map((tag) => (
                  <Col
                    key={tag.id}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <div
                      style={{
                        padding: '5px',
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      <Checkbox value={tag.id}>
                        {tag.name}{' '}
                        {tag.position && (
                          <span
                            style={{ fontWeight: '400', marginLeft: '5px' }}
                          >
                            ({tag?.position})
                          </span>
                        )}
                      </Checkbox>
                    </div>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Type Modal */}
      <Modal
        title={t('vehicle.actions.chooseImageType')}
        open={isTypeModalVisible}
        onCancel={() => setIsTypeModalVisible(false)}
        onOk={handleTypeSubmit}
        okText={t('common.ok')}
        cancelText={t('common.cancel')}
      >
        <Form form={form}>
          <Form.Item name="selectedType">
            <Radio.Group>
              <Radio value="interior">{t('vehicles.imageType.interior')}</Radio>
              <Radio value="exterior">{t('vehicles.imageType.exterior')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </Col>
  );
};

export default VehicleImageItem;
